import React, { useState } from 'react';
import { getModelPerformanceDataByModelId, getModelPerformance, getModelSummary, getEvaluationFlags } from '../api';
import { useActiveModel } from '../context/ActiveModelContext';
import { EmptyState, InfoTooltip, ErrorState } from './shared/UIComponents';
import LoadingSpinner from './LoadingSpinner';

function ModelPerformance() {
  const [modelPerformance, setModelPerformance] = useState(null);
  const [modelSummary, setModelSummary] = useState(null);
  const [modelFlags, setModelFlags] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { activeModel } = useActiveModel();
  const taskType = activeModel?.metadata?.task_type || activeModel?.task_type || 'unknown';

  const handleGetModelPerformance = async () => {
    setLoading(true);
    setError(null);
    setModelPerformance(null);
    try {
      if (activeModel?.id && activeModel?.dataset_name) {
        const res = await getModelPerformanceDataByModelId(activeModel.dataset_name, activeModel.id);
        const payload = res?.data ?? res;
        setModelPerformance(payload);
      } else {
        // Fallback to dataset-level only if no active model is available
        const result = await getModelPerformance();
        setModelPerformance(result);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGetModelSummary = async () => {
    setLoading(true);
    setError(null);
    setModelSummary(null);
    try {
      if (activeModel?.id && activeModel?.dataset_name) {
        const res = await getModelSummary(activeModel.dataset_name, activeModel.id);
        const payload = res?.data ?? res;
        setModelSummary(payload);
      } else {
        setError("No active model selected to get summary");
      }
    }
    catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleGetModelFlags = async () => {
    setLoading(true);
    setError(null);
    setModelFlags(null);
    try {
      if (activeModel?.id && activeModel?.dataset_name) {
        const res = await getEvaluationFlags(activeModel.dataset_name, activeModel.id);
        const payload = res?.data ?? res;
        setModelFlags(payload);
      }
      else {
        setError("No active model selected to get evaluation flags");
      }
    }
    catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const renderSummary = (summaryString) => {
  const metrics = summaryString.split('\n').map(line => line.split(': '));

  return (
    <div className="metrics-grid">
      {metrics.map(([label, value]) => (
        <div key={label} className="metric-item">
          <span>{label}: </span>
          <span>{value}</span>
        </div>
      ))}
    </div>
  );
};

const renderFlags = (flags) => (
  <ul className="flags-list">
    {flags.map((flag, index) => (
      <li key={index}>{flag}</li>
    ))}
  </ul>
);


  return (
    <div className="model-performance-container">
      <div className="model-performance">
        <InfoTooltip 
          title="Model Performance" 
          content="View detailed performance metrics from your most recently trained model. Includes accuracy, precision, recall, F1-score, and other relevant metrics."
        >
          <h2 className="model-performance__title">Model Performance</h2>
        </InfoTooltip>

        <div style={{ marginBottom: 12, fontSize: 13, color: '#555' }}>
          <strong>Active model:</strong> {activeModel?.id || 'Not selected'} | <strong>Task:</strong> {taskType}
          {taskType === 'classification' && (
            <div style={{ marginTop: 4 }}>
              Focus on discrimination metrics (accuracy, precision, recall, F1, ROC AUC) and false negative risk.
            </div>
          )}
          {taskType === 'regression' && (
            <div style={{ marginTop: 4 }}>
              Focus on fit/error metrics (R2, MSE, MAE, RMSE). Lower error metrics indicate better calibration.
            </div>
          )}
        </div>
        
        <button 
          onClick={handleGetModelPerformance} 
          disabled={loading}
          className="model-performance__load-btn"
        >
          {loading ? 'Loading...' : 'Get Model Performance'}
        </button>
        
        {loading && (
          <div className="model-performance__loading">
            <LoadingSpinner text="Loading model performance..." />
          </div>
        )}
        
        {error && (
          <ErrorState 
            message={error} 
            onRetry={() => {
              setError(null);
              handleGetModelPerformance();
            }}
          />
        )}
        
        {!modelPerformance && !loading && !error && (
          <EmptyState 
            message={activeModel?.id
              ? 'Click the button above to load model-specific performance metrics.'
              : 'Select an active model first, then load model-specific performance metrics.'}
            icon="📊"
          />
        )}
        
        {modelPerformance && (
          <div className="model-performance__results">
            <h3 className="model-performance__results-title">Model Performance Metrics</h3>
            <pre className="model-performance__results-json">
              {JSON.stringify(modelPerformance, null, 2)}
            </pre>
          </div>
        )}
      </div>
      <div className="model-summary">
        <InfoTooltip 
          title="Model Summary" 
          content="View a summary of your most recently trained model's performance."
        >
          <h2 className="model-performance__title">Model Summary</h2>
        </InfoTooltip>
        
        <button 
          onClick={handleGetModelSummary} 
          disabled={loading}
          className="model-performance__load-btn"
        >
          {loading ? 'Loading...' : 'Get Model Summary'}
        </button>
        
        {modelSummary && (
          <div className="model-summary__results">
            <h3 className="model-summary__results-title">Model Summary</h3>
            {renderSummary(modelSummary)}
          </div>
        )}
      </div>
      <div className="model-flags">
        <InfoTooltip 
          title="Model Flags" 
          content="View any evaluation flags associated with your most recently trained model."
        >
          <h2 className="model-performance__title">Model Flags</h2>
        </InfoTooltip>
        <button 
          onClick={handleGetModelFlags} 
          disabled={loading}
          className="model-performance__load-btn"
        >
          {loading ? 'Loading...' : 'Get Evaluation Flags'}
        </button>
        
        {modelFlags && (
          <div className="model-flags__results">
            <h3 className="model-flags__results-title">Evaluation Flags</h3>
            {renderFlags(modelFlags)}
          </div>
        )}
      </div>
    </div>
    );
}

export default ModelPerformance;
